/**
 * Agora Brain — Unified Command Center
 *
 * Single scrollable page. No sub-tabs. Two clear sections:
 *   ① Actions Required  — things needing a human decision
 *   ② Automation Agents — background agents (status + manual trigger)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Eye, FileText, Bell, AlertTriangle, Activity, ShoppingCart,
  Mail, RefreshCw, Zap, CheckCircle, X, Play, Plus,
  ChevronDown, ChevronRight, Clock, Package, History, Phone, XCircle
} from 'lucide-react';
import { StatCard, HelpTip } from '../WMSShared';
import ProcurementPipeline from '../agent/ProcurementPipeline';

const API    = process.env.REACT_APP_BACKEND_URL + '/api/wms';
const AUTO   = `${API}/automation`;

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, color, title, count, tip, children }) => (
  <div className="flex items-center justify-between mb-4 mt-2">
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 text-${color}-600`} />
      </div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      {count !== undefined && count > 0 && (
        <span className={`text-xs px-2 py-0.5 rounded-full font-bold bg-${color}-100 text-${color}-700`}>{count}</span>
      )}
      {tip && <HelpTip text={tip} />}
    </div>
    {children}
  </div>
);

const EmptyCard = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3 px-5 py-4 text-gray-400">
    <Icon className="w-5 h-5 shrink-0" />
    <span className="text-sm">{text}</span>
  </div>
);

// ─── Confirm Delivery Modal ────────────────────────────────────────────────────

function ConfirmModal({ delivery, onConfirm, onClose, cur = 'S$' }) {
  const [qty, setQty]     = useState(delivery.quantity);
  const [unitCost, setUnitCost] = useState(String(delivery.unit_cost || 0));
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const qtyDiff = qty !== delivery.quantity;
  const costNum = parseFloat(unitCost) || 0;
  const priceDiff = Math.abs(costNum - (delivery.unit_cost || 0)) > 0.005;
  const hasDiscrepancy = qtyDiff || priceDiff;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-500" />Confirm Delivery</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <p className="font-semibold">{delivery.product_name}</p>
            <p className="text-xs text-gray-500">PO: {delivery.purchase_id} · Supplier: {delivery.supplier}</p>
            <p className="text-xs text-gray-500">Expected: {delivery.quantity} units @ {cur}{delivery.unit_cost?.toLocaleString()} = {cur}{delivery.total_cost?.toLocaleString()}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Actual Qty Received *</label>
              <input type="number" min="1" value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${qtyDiff ? 'border-amber-400 bg-amber-50' : ''}`} data-testid="confirm-qty" />
              {qtyDiff && <p className="text-[11px] text-amber-600 mt-1">{qty > delivery.quantity ? '+' : ''}{qty - delivery.quantity} vs ordered ({delivery.quantity})</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Actual Unit Cost ({cur})</label>
              <input type="number" min="0" step="0.01" value={unitCost} onChange={e => setUnitCost(e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${priceDiff ? 'border-amber-400 bg-amber-50' : ''}`} data-testid="confirm-cost" />
              {priceDiff && <p className="text-[11px] text-amber-600 mt-1">PO price: {cur}{delivery.unit_cost?.toLocaleString()}</p>}
            </div>
          </div>
          {hasDiscrepancy && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Discrepancy Reason</label>
              <select value={reason} onChange={e => setReason(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="confirm-reason">
                <option value="">Select reason...</option>
                <option value="over_delivery">Over-delivery (supplier shipped extra)</option>
                <option value="under_delivery">Under-delivery (partial shipment)</option>
                <option value="price_change">Price change from supplier</option>
                <option value="damaged_goods">Damaged goods (partial rejection)</option>
                <option value="wrong_item">Wrong item received</option>
                <option value="supplier_error">Supplier error</option>
                <option value="negotiated">Negotiated price</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Notes (optional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Condition, invoice ref, etc."
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="confirm-notes" />
          </div>
          <div className={`rounded-lg p-3 text-xs ${hasDiscrepancy ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {hasDiscrepancy ? (
              <p>Discrepancy detected. Confirming adds {qty} units at {cur}{costNum.toLocaleString()} (total: {cur}{(qty * costNum).toLocaleString()}) and creates an alert for review.</p>
            ) : (
              <p>Confirming adds {qty} units to stock and calculates new weighted average cost.</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
          <button disabled={saving} onClick={async () => { setSaving(true); try { await onConfirm(delivery, qty, notes, costNum, reason); } catch { setSaving(false); } }}
            className="px-5 py-2 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:bg-gray-300 font-medium"
            data-testid="confirm-receipt-btn">
            {saving ? 'Confirming...' : 'Confirm Receipt'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function AgentTab({
  API: apiProp, agentData, agentLoading, notifSettings, setNotifSettings,
  runAgentScan, loadInsights, approveDraftPO, rejectDraftPO, loadAgent, user,
  cur = 'S$', poActionLoading = null, justApprovedId = null
}) {
  // Automation state (previously in AutomationTab)
  const [deliveries, setDeliveries]         = useState([]);
  const [fulfillQueue, setFulfillQueue]     = useState([]);
  const [autoSettings, setAutoSettings]     = useState({});
  const [confirmModal, setConfirmModal]     = useState(null);
  const [runLoading, setRunLoading]         = useState({});
  const [runResults, setRunResults]         = useState({});
  const [showNotif, setShowNotif]           = useState(false);
  const [showLog, setShowLog]               = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [editingPO, setEditingPO]           = useState(null);
  const [poEdits, setPoEdits]               = useState({});
  const [sendingPO, setSendingPO]           = useState(null);
  const [sendEmail, setSendEmail]           = useState('');
  const [sendMsg, setSendMsg]               = useState({});
  const [cancelModal, setCancelModal]       = useState(null);
  const [actionLoading, setActionLoading]   = useState(false);
  const [showCreatePO, setShowCreatePO]     = useState(false);
  const [productSuppliers, setProductSuppliers] = useState([]);
  const [newPO, setNewPO]                   = useState({ product_id: '', supplier_id: '', quantity: '', unit_cost: '', reason: '' });
  const [createLoading, setCreateLoading]   = useState(false);
  const [productsList, setProductsList]     = useState([]);
  const [suppliersList, setSuppliersList]   = useState([]);
  const [notifHistory, setNotifHistory]     = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [phoneError, setPhoneError]         = useState('');
  const [phoneSaving, setPhoneSaving]       = useState(false);
  const [digestEnabled, setDigestEnabled]   = useState(false);
  const [digestLoading, setDigestLoading]   = useState(false);
  const [digestSending, setDigestSending]   = useState(false);
  const [digestMsg, setDigestMsg]           = useState('');
  const [digestSchedule, setDigestSchedule] = useState({
    frequency: 'weekly', hour: '8', minute: '0', day_of_week: '0', day_of_month: '1', template: 'full',
  });
  const [digestSaving, setDigestSaving]     = useState(false);
  const [previewHtml, setPreviewHtml]       = useState('');
  const [previewStats, setPreviewStats]     = useState(null);
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState('full');

  const handleSendPO = async (draftId) => {
    try {
      setSendMsg(prev => ({ ...prev, [draftId]: { type: 'loading' } }));
      await axios.post(`${AUTO}/draft-pos/${draftId}/send-to-supplier`, { email: sendEmail });
      setSendMsg(prev => ({ ...prev, [draftId]: null }));
      setSendingPO(null);
      setSendEmail('');
      loadAgent();
    } catch (err) {
      setSendMsg(prev => ({ ...prev, [draftId]: { type: 'error', text: err.response?.data?.detail || 'Failed to send' } }));
    }
  };

  const [cancelLoading, setCancelLoading]   = useState(false);

  const cancelPO = async (draftId) => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      await axios.put(`${API}/agent/draft-pos/${draftId}/cancel`, { cancelled_by: user?.email, reason: 'Cancelled by manager' }, { timeout: 10000 });
    } catch {}
    await loadAgent();
    await new Promise(r => setTimeout(r, 500));
    setCancelLoading(false);
    setCancelModal(null);
  };

  const cancelDO = async (deliveryId) => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      await axios.put(`${API}/agent/deliveries/${deliveryId}/cancel`, { cancelled_by: user?.email, reason: 'Cancelled by manager' }, { timeout: 10000 });
    } catch {}
    await loadAgent();
    await loadAutomation();
    await new Promise(r => setTimeout(r, 500));
    setCancelLoading(false);
    setCancelModal(null);
  };

  const restorePO = async (draftId) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/agent/draft-pos/${draftId}/restore`, { restored_by: user?.email });
      loadAgent();
    } catch {} finally { setActionLoading(false); }
  };

  const restoreDO = async (deliveryId) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/agent/deliveries/${deliveryId}/restore`, { restored_by: user?.email });
      loadAgent();
    } catch {} finally { setActionLoading(false); }
  };

  const openCreatePO = async () => {
    setShowCreatePO(true);
    try {
      const [pRes, sRes] = await Promise.all([axios.get(`${API}/products`), axios.get(`${API}/suppliers`)]);
      setProductsList(pRes.data);
      setSuppliersList(sRes.data);
    } catch {}
  };

  const submitCreatePO = async () => {
    if (createLoading) return;
    setCreateLoading(true);
    try {
      await axios.post(`${API}/agent/draft-pos`, { ...newPO, created_by: user?.email }, { timeout: 10000 });
      setShowCreatePO(false);
      setNewPO({ product_id: '', supplier_id: '', quantity: '', unit_cost: '', reason: '' });
      loadAgent();
    } catch (err) { window.__wmsToast?.(err.response?.data?.detail || 'Failed to create PO'); }
    finally { setCreateLoading(false); }
  };

  const loadAutomation = async () => {
    try {
      const [d, q, s] = await Promise.all([
        axios.get(`${AUTO}/deliveries`),
        axios.get(`${AUTO}/fulfillment/queue`),
        axios.get(`${AUTO}/settings`),
      ]);
      setDeliveries(d.data);
      setFulfillQueue(q.data);
      setAutoSettings(s.data);
    } catch {}
  };

  useEffect(() => { loadAutomation(); loadDigestSettings(); }, []);
  useEffect(() => { loadAutomation(); }, [agentData]);

  const loadDigestSettings = async () => {
    try {
      const r = await axios.get(`${API}/agent/notifications/digest-settings`);
      setDigestEnabled(r.data.enabled === 'true');
      setDigestSchedule({
        frequency: r.data.frequency || 'weekly',
        hour: r.data.hour || '8',
        minute: r.data.minute || '0',
        day_of_week: r.data.day_of_week || '0',
        day_of_month: r.data.day_of_month || '1',
        template: r.data.template || 'full',
      });
    } catch {}
  };

  const toggleDigest = async () => {
    setDigestLoading(true);
    const newVal = !digestEnabled;
    try {
      await axios.put(`${API}/agent/notifications/digest-settings`, { enabled: newVal });
      setDigestEnabled(newVal);
    } catch {}
    setDigestLoading(false);
  };

  const saveDigestSchedule = async () => {
    setDigestSaving(true);
    try {
      await axios.put(`${API}/agent/notifications/digest-settings`, digestSchedule);
      setDigestMsg('Schedule saved!');
      setTimeout(() => setDigestMsg(''), 3000);
    } catch { setDigestMsg('Failed to save'); }
    setDigestSaving(false);
  };

  const sendDigestNow = async () => {
    setDigestSending(true); setDigestMsg('');
    try {
      const r = await axios.post(`${API}/agent/notifications/send-digest`);
      setDigestMsg(r.data.message || 'Digest sent!');
    } catch (e) {
      setDigestMsg(e.response?.data?.detail || e.response?.data?.message || 'Failed to send digest');
    }
    setDigestSending(false);
  };

  const loadDigestPreview = async (tpl) => {
    const t = tpl || digestSchedule.template || 'full';
    setPreviewLoading(true);
    setPreviewTemplate(t);
    try {
      const r = await axios.get(`${API}/agent/notifications/digest-preview?template=${t}`);
      setPreviewHtml(r.data.html);
      setPreviewStats(r.data.stats);
      setPreviewOpen(true);
    } catch { setDigestMsg('Failed to load preview'); }
    setPreviewLoading(false);
  };

  const openDigestInNewTab = (tpl) => {
    const t = tpl || previewTemplate || digestSchedule.template || 'full';
    window.open(`${API}/agent/notifications/digest-preview/html?template=${t}`, '_blank');
  };

  const loadNotifHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/agent/notifications/history?limit=30`);
      setNotifHistory(r.data);
    } catch {}
    setHistoryLoading(false);
  };

  const validateAndSavePhone = async () => {
    const phone = notifSettings.notification_sms_to || '';
    if (!phone) { setPhoneError('Enter a phone number'); return; }
    setPhoneSaving(true); setPhoneError('');
    try {
      const v = await axios.post(`${API}/agent/notifications/validate-phone`, { phone });
      if (!v.data.valid) { setPhoneError(v.data.error); setPhoneSaving(false); return; }
      await axios.put(`${API}/agent/notifications/settings`, { notification_sms_to: v.data.formatted });
      setNotifSettings(p => ({ ...p, notification_sms_to: v.data.formatted }));
      setPhoneError('');
    } catch (e) {
      setPhoneError(e.response?.data?.detail || 'Validation failed');
    }
    setPhoneSaving(false);
  };

  const runAgent = async (key, fn) => {
    setRunLoading(p => ({ ...p, [key]: true }));
    setRunResults(p => ({ ...p, [key]: null }));
    try { const r = await fn(); setRunResults(p => ({ ...p, [key]: r.data })); loadAgent(); loadAutomation(); }
    catch (e) { setRunResults(p => ({ ...p, [key]: { error: e.response?.data?.detail || 'Failed' } })); }
    setRunLoading(p => ({ ...p, [key]: false }));
  };

  const confirmDelivery = async (delivery, qty, notes, actualUnitCost, discrepancyReason) => {
    await axios.post(`${AUTO}/deliveries/${delivery.delivery_id}/confirm`, {
      confirmed_by: user?.email || 'staff', actual_quantity: parseInt(qty), notes,
      actual_unit_cost: parseFloat(actualUnitCost) || delivery.unit_cost,
      discrepancy_reason: discrepancyReason || '',
    });
    setConfirmModal(null);
    await loadAutomation();
    await loadAgent();
    // Scroll to confirmed deliveries section
    setTimeout(() => {
      const el = document.getElementById(`do-${delivery.delivery_id}`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-emerald-400'); setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400'), 3000); }
    }, 300);
  };

  const saveSetting = async (key, value) => {
    await axios.put(`${AUTO}/settings`, { [key]: value });
    setAutoSettings(p => ({ ...p, [key]: value }));
    loadAutomation();
  };

  // Derived counts
  const pendingDrafts       = agentData.drafts.filter(d => d.status === 'pending');
  const processedDrafts     = agentData.drafts.filter(d => d.status === 'rejected' || d.status === 'approved' || d.status === 'converted' || d.status === 'cancelled');
  const pendingDeliveries   = deliveries.filter(d => d.status === 'pending' || d.status === 'overdue');
  const confirmedDeliveries = deliveries.filter(d => d.status === 'confirmed');
  const cancelledDeliveries = deliveries.filter(d => d.status === 'cancelled' || d.status === 'rejected');
  const blockedOrders       = fulfillQueue.filter(o => !o.can_fulfill);
  const readyOrders         = fulfillQueue.filter(o => o.can_fulfill);
  const actionsNeeded       = pendingDrafts.length + pendingDeliveries.length;

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Eye className="w-6 h-6 text-purple-600" />Agora Brain</h2>
          <p className="text-sm text-gray-500">Autonomous AI Operations Agent — monitors, predicts, and acts</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadInsights} disabled={agentLoading.insights}
            className="flex items-center gap-2 text-sm border px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <Activity className={`w-4 h-4 ${agentLoading.insights ? 'animate-spin' : ''}`} />
            {agentLoading.insights ? 'Thinking...' : 'AI Insights'}
          </button>
          <button onClick={runAgentScan} disabled={agentLoading.scan}
            className="flex items-center gap-2 text-sm bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:bg-gray-400 shadow-sm">
            <Eye className={`w-4 h-4 ${agentLoading.scan ? 'animate-pulse' : ''}`} />
            {agentLoading.scan ? 'Scanning...' : 'Run Agent Scan'}
          </button>
        </div>
      </div>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Pending Draft POs"   value={agentData.stats.pending_draft_pos  || 0} icon={FileText}     color="purple" sub="Awaiting approval" />
        <StatCard label="Unread Alerts"        value={agentData.stats.unacknowledged_alerts || 0} icon={Bell}      color="orange" sub="From agent scans" />
        <StatCard label="Products at Risk"     value={agentData.stats.products_at_risk   || 0} icon={AlertTriangle} color="red"  sub="Low/out of stock" />
        <StatCard label="Total Scans"          value={agentData.stats.total_scans        || 0} icon={Eye}          color="blue" sub={agentData.stats.last_scan ? `Last: ${agentData.stats.last_scan.split('T')[0]}` : 'Never'} />
      </div>

      {/* AI Insights */}
      {agentData.insights && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-5">
          <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-3"><Activity className="w-4 h-4" />AI Operations Report</h3>
          <div className="text-sm text-purple-900 whitespace-pre-wrap">{agentData.insights}</div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 1: ACTIONS REQUIRED
      ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className={`px-5 py-4 border-b ${actionsNeeded > 0 ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-gray-50'}`}>
          <SectionHeader icon={Clock} color={actionsNeeded > 0 ? 'orange' : 'gray'}
            title="Actions Required"
            count={actionsNeeded}
            tip="Everything needing your decision: POs to approve, deliveries to confirm, orders blocked by stock.">
            {actionsNeeded === 0 && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />All caught up</span>}
          </SectionHeader>
        </div>

        {actionsNeeded === 0 && readyOrders.length === 0 && blockedOrders.length === 0 ? (
          <EmptyCard icon={CheckCircle} text="Nothing needs your attention right now. Run Agent Scan to check for new issues." />
        ) : (
          <div className="divide-y">

            {/* Unified Procurement Pipeline (PO-centric, industry-standard
                WMS pattern: each PO owns its lifecycle Draft → Approved →
                In Transit → Received → Done, with linked deliveries and
                activity nested as expandable detail. Replaces three separate
                vertical lists). */}
            <ProcurementPipeline
              drafts={[...pendingDrafts, ...processedDrafts]}
              deliveries={[...pendingDeliveries, ...confirmedDeliveries, ...cancelledDeliveries]}
              cur={cur}
              API={API}
              openCreatePO={openCreatePO}
              suppliersList={suppliersList}
              setSuppliersList={setSuppliersList}
              poActionLoading={poActionLoading}
              editingPO={editingPO}
              setEditingPO={setEditingPO}
              poEdits={poEdits}
              setPoEdits={setPoEdits}
              approveDraftPO={approveDraftPO}
              rejectDraftPO={rejectDraftPO}
              sendingPO={sendingPO}
              setSendingPO={setSendingPO}
              sendEmail={sendEmail}
              setSendEmail={setSendEmail}
              sendMsg={sendMsg}
              handleSendPO={handleSendPO}
              setCancelModal={setCancelModal}
              restorePO={restorePO}
              restoreDO={restoreDO}
              setConfirmModal={setConfirmModal}
            />

            {/* Blocked Orders */}
            {blockedOrders.length > 0 && (
              <div className="px-5 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1">
                  <Package className="w-3.5 h-3.5" />Orders Blocked — waiting for stock
                  <HelpTip text="These orders can't be packed because a product is out of stock. They'll auto-advance to 'packed' once stock arrives and you confirm the delivery above." />
                </p>
                <div className="space-y-2">
                  {blockedOrders.map(o => (
                    <div key={o.order_id} className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 bg-gray-50">
                      <div>
                        <span className="text-sm font-medium font-mono">{o.order_id}</span>
                        <span className="text-xs text-gray-500 ml-2">{o.customer_name}</span>
                        <p className="text-xs text-red-600 mt-0.5">Missing: {o.stock_issues?.join(', ')}</p>
                      </div>
                      <span className={`text-xs ${o.age_hours > 24 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{o.age_hours}h old</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ready orders (informational) */}
            {readyOrders.length > 0 && (
              <div className="px-5 py-4 bg-emerald-50/50">
                <p className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <strong>{readyOrders.length} order(s)</strong> ready to pack — fulfillment agent will process them automatically in the next 10 min cycle.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2: AUTOMATION AGENTS
      ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
          <SectionHeader icon={Zap} color="purple" title="Automation Agents"
            tip="These three agents run in the background on a schedule. You can also trigger them manually with Run Now." />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">

          {/* Agent 1: Scan */}
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-sm flex items-center gap-1"><Eye className="w-4 h-4 text-purple-500" />Inventory Scanner</p>
                <p className="text-xs text-gray-500 mt-0.5">Checks every product for low stock. Creates draft POs automatically.</p>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mb-3">Runs every hour · Last: {agentData.stats.last_scan ? agentData.stats.last_scan.split('T')[0] : 'Never'}</p>
            <button onClick={() => runAgent('scan', () => { runAgentScan(); return Promise.resolve({ data: { ok: true } }); })}
              disabled={agentLoading.scan}
              className="w-full flex items-center justify-center gap-2 bg-purple-500 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-purple-600 disabled:bg-gray-300">
              <Play className={`w-3 h-3 ${agentLoading.scan ? 'animate-pulse' : ''}`} />
              {agentLoading.scan ? 'Scanning...' : 'Run Now'}
            </button>
          </div>

          {/* Agent 2: Auto-Procurement */}
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-sm flex items-center gap-1"><Zap className="w-4 h-4 text-emerald-500" />Auto-Procurement</p>
                <p className="text-xs text-gray-500 mt-0.5">Auto-approves POs under your threshold. POs above threshold appear in Actions Required.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] text-gray-500">Threshold {cur}</span>
              <input type="number" value={autoSettings.auto_procurement_threshold || 2000}
                onChange={e => setAutoSettings(p => ({ ...p, auto_procurement_threshold: e.target.value }))}
                className="w-20 border rounded px-2 py-1 text-xs" />
              <button onClick={() => saveSetting('auto_procurement_threshold', autoSettings.auto_procurement_threshold)} className="text-[10px] bg-gray-100 px-2 py-1 rounded hover:bg-gray-200">Save</button>
              <button onClick={() => saveSetting('auto_procurement_enabled', autoSettings.auto_procurement_enabled === 'true' ? 'false' : 'true')}
                className={`text-[10px] px-2 py-1 rounded font-medium ${autoSettings.auto_procurement_enabled === 'true' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {autoSettings.auto_procurement_enabled === 'true' ? 'ON' : 'OFF'}
              </button>
            </div>
            <button onClick={() => runAgent('procurement', () => axios.post(`${AUTO}/procurement/run`))}
              disabled={runLoading.procurement}
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-emerald-600 disabled:bg-gray-300">
              <Play className={`w-3 h-3 ${runLoading.procurement ? 'animate-pulse' : ''}`} />
              {runLoading.procurement ? 'Running...' : 'Run Now'}
            </button>
            {runResults.procurement && !runResults.procurement.error && (
              <p className="text-[10px] text-emerald-700 mt-2">✓ {runResults.procurement.auto_approved} auto-approved · {runResults.procurement.requires_approval} need review</p>
            )}
          </div>

          {/* Agent 3: Fulfillment */}
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-sm flex items-center gap-1"><ShoppingCart className="w-4 h-4 text-blue-500" />Order Fulfillment</p>
                <p className="text-xs text-gray-500 mt-0.5">Auto-packs orders when stock is available. Moves them to "packed" status.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] text-gray-400">Runs every 10 min ·</span>
              <button onClick={() => saveSetting('auto_fulfillment_enabled', autoSettings.auto_fulfillment_enabled === 'false' ? 'true' : 'false')}
                className={`text-[10px] px-2 py-1 rounded font-medium ${autoSettings.auto_fulfillment_enabled !== 'false' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {autoSettings.auto_fulfillment_enabled !== 'false' ? 'ON' : 'OFF'}
              </button>
            </div>
            <button onClick={() => runAgent('fulfillment', () => axios.post(`${AUTO}/fulfillment/run`))}
              disabled={runLoading.fulfillment}
              className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-blue-600 disabled:bg-gray-300">
              <Play className={`w-3 h-3 ${runLoading.fulfillment ? 'animate-pulse' : ''}`} />
              {runLoading.fulfillment ? 'Running...' : 'Run Now'}
            </button>
            {runResults.fulfillment && !runResults.fulfillment.error && (
              <p className="text-[10px] text-blue-700 mt-2">✓ {runResults.fulfillment.fulfilled} packed · {runResults.fulfillment.blocked} blocked</p>
            )}
          </div>
        </div>

        {/* Shopee status */}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center gap-3">
          <ShoppingCart className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs text-gray-500">Shopee: Simulator mode active ·</span>
          <span className="text-xs text-gray-400">Manage orders in the <strong className="text-orange-600">Shopee</strong> sidebar tab</span>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3: ACTIVITY LOG (collapsible)
      ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button onClick={() => setShowLog(v => !v)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-sm">Activity Log</span>
            {agentData.stats.unacknowledged_alerts > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{agentData.stats.unacknowledged_alerts} unread</span>
            )}
          </div>
          {showLog ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {showLog && (
          <>
            <div className="px-5 py-2 border-t border-b bg-gray-50 flex justify-end">
              <button onClick={async () => { await axios.put(`${API}/agent/actions/acknowledge-all`); loadAgent(); }} className="text-xs text-gray-500 hover:text-gray-700">Mark all read</button>
            </div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {agentData.actions.map((a, i) => (
                <div key={i} className={`flex items-start gap-3 px-5 py-3 ${!a.acknowledged ? 'bg-orange-50/40' : ''}`}>
                  <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${a.severity === 'critical' ? 'bg-red-500' : a.severity === 'high' ? 'bg-orange-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{a.details}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{a.action_type} · {a.timestamp?.split('.')[0]?.replace('T', ' ')}</p>
                  </div>
                  {!a.acknowledged && (
                    <button onClick={async () => { await axios.put(`${API}/agent/actions/${a.action_id}/acknowledge`); loadAgent(); }} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">Dismiss</button>
                  )}
                </div>
              ))}
              {agentData.actions.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">No activity yet. Run a scan to start monitoring.</div>}
            </div>
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4: NOTIFICATIONS — Email + SMS
      ═══════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button onClick={() => setShowNotif(v => !v)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-sm">Notifications</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${notifSettings.notification_email_enabled === 'true' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
              Email {notifSettings.notification_email_enabled === 'true' ? 'On' : 'Off'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${notifSettings.notification_sms_enabled === 'true' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
              SMS {notifSettings.notification_sms_enabled === 'true' ? 'On' : 'Off'}
            </span>
          </div>
          {showNotif ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {showNotif && (
          <div className="p-5 border-t space-y-5">
            {/* Email */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />Email (Gmail SMTP)
              </p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Critical stock + draft PO alerts</span>
                <button onClick={async () => {
                  const v = notifSettings.notification_email_enabled === 'true' ? 'false' : 'true';
                  await axios.put(`${API}/agent/notifications/settings`, { notification_email_enabled: v });
                  setNotifSettings(p => ({ ...p, notification_email_enabled: v }));
                }} data-testid="email-notif-toggle"
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${notifSettings.notification_email_enabled === 'true' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {notifSettings.notification_email_enabled === 'true' ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <div className="flex gap-2">
                <input type="email" value={notifSettings.notification_email_to || ''} onChange={e => setNotifSettings(p => ({ ...p, notification_email_to: e.target.value }))}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="your@email.com"
                  data-testid="email-notif-recipient" />
                <button onClick={async () => { await axios.put(`${API}/agent/notifications/settings`, { notification_email_to: notifSettings.notification_email_to }); }}
                  className="text-xs bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600">Save</button>
                <button onClick={async () => { try { await axios.post(`${API}/agent/notifications/test-email`); window.__wmsToast?.('Test email sent!', 'success'); } catch (e) { window.__wmsToast?.(e.response?.data?.detail || 'Failed'); } }}
                  className="text-xs border px-3 py-2 rounded-lg hover:bg-gray-50">Test</button>
              </div>
              {!notifSettings.gmail_configured && <p className="text-xs text-amber-600 mt-2">Add GMAIL_USER + GMAIL_APP_PASSWORD to .env to enable email sending.</p>}
            </div>

            <div className="border-t" />

            {/* SMS */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" />SMS (Twilio)
              </p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Instant SMS for critical alerts</span>
                <button onClick={async () => {
                  const v = notifSettings.notification_sms_enabled === 'true' ? 'false' : 'true';
                  await axios.put(`${API}/agent/notifications/settings`, { notification_sms_enabled: v });
                  setNotifSettings(p => ({ ...p, notification_sms_enabled: v }));
                }} data-testid="sms-notif-toggle"
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${notifSettings.notification_sms_enabled === 'true' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {notifSettings.notification_sms_enabled === 'true' ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <div className="flex gap-2">
                <input type="tel" value={notifSettings.notification_sms_to || ''}
                  onChange={e => { setNotifSettings(p => ({ ...p, notification_sms_to: e.target.value })); setPhoneError(''); }}
                  className={`flex-1 border rounded-lg px-3 py-2 text-sm font-mono ${phoneError ? 'border-red-300 bg-red-50' : ''}`}
                  placeholder="+6591234567"
                  data-testid="sms-notif-recipient" />
                <button onClick={validateAndSavePhone} disabled={phoneSaving}
                  data-testid="save-sms-btn"
                  className="text-xs bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-300">
                  {phoneSaving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={async () => {
                  try { await axios.post(`${API}/agent/notifications/test-sms`); window.__wmsToast?.('Test SMS sent!', 'success'); }
                  catch (e) { window.__wmsToast?.(e.response?.data?.detail || 'SMS failed'); }
                }} data-testid="test-sms-btn" className="text-xs border px-3 py-2 rounded-lg hover:bg-gray-50">Test</button>
              </div>
              {phoneError && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1" data-testid="phone-error">
                  <XCircle className="w-3 h-3 shrink-0" />{phoneError}
                </p>
              )}
              {!phoneError && !notifSettings.twilio_configured
                ? <p className="text-xs text-amber-600 mt-2">Add TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_FROM_NUMBER to .env to enable SMS.</p>
                : !phoneError && <p className="text-xs text-emerald-600 mt-2">Twilio credentials detected. Format: +[country code][number] (e.g. +6591234567)</p>
              }
            </div>

            <div className="border-t" />

            {/* Scheduled Digest */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />Scheduled Digest
              </p>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm">Operational summary with critical highlights</span>
                  <p className="text-xs text-gray-400 mt-0.5">Orders, revenue, stock health, pending POs, agent activity — sent to your email recipient</p>
                </div>
                <button onClick={toggleDigest} disabled={digestLoading}
                  data-testid="digest-toggle"
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${digestEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {digestLoading ? '...' : digestEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              {/* Schedule picker */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Frequency</label>
                    <select value={digestSchedule.frequency}
                      onChange={e => setDigestSchedule(p => ({ ...p, frequency: e.target.value }))}
                      data-testid="digest-frequency"
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {digestSchedule.frequency === 'weekly' && (
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Day</label>
                      <select value={digestSchedule.day_of_week}
                        onChange={e => setDigestSchedule(p => ({ ...p, day_of_week: e.target.value }))}
                        data-testid="digest-day-of-week"
                        className="border rounded-lg px-3 py-1.5 text-sm bg-white">
                        {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d,i) => (
                          <option key={i} value={String(i)}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {digestSchedule.frequency === 'monthly' && (
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Day of Month</label>
                      <select value={digestSchedule.day_of_month}
                        onChange={e => setDigestSchedule(p => ({ ...p, day_of_month: e.target.value }))}
                        data-testid="digest-day-of-month"
                        className="border rounded-lg px-3 py-1.5 text-sm bg-white">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={String(d)}>{d}{d===1?'st':d===2?'nd':d===3?'rd':'th'}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Hour (SGT)</label>
                    <select value={digestSchedule.hour}
                      onChange={e => setDigestSchedule(p => ({ ...p, hour: e.target.value }))}
                      data-testid="digest-hour"
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white">
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={String(i)}>{String(i).padStart(2,'0')}:00</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Minute</label>
                    <select value={digestSchedule.minute}
                      onChange={e => setDigestSchedule(p => ({ ...p, minute: e.target.value }))}
                      data-testid="digest-minute"
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white">
                      {[0,5,10,15,20,25,30,35,40,45,50,55].map(m => (
                        <option key={m} value={String(m)}>:{String(m).padStart(2,'0')}</option>
                      ))}
                    </select>
                  </div>

                  <button onClick={saveDigestSchedule} disabled={digestSaving}
                    data-testid="save-digest-schedule"
                    className="self-end text-xs bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 font-medium">
                    {digestSaving ? 'Saving...' : 'Save Schedule'}
                  </button>
                </div>

                {/* Template selector */}
                <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                  <label className="text-[10px] text-gray-500 shrink-0">Template</label>
                  <div className="flex gap-1.5">
                    {[
                      { id: 'executive', label: 'Executive Summary', desc: 'Compact KPIs' },
                      { id: 'full', label: 'Full Operations', desc: 'Everything' },
                      { id: 'critical', label: 'Critical Only', desc: 'Alerts only' },
                    ].map(t => (
                      <button key={t.id}
                        onClick={() => setDigestSchedule(p => ({ ...p, template: t.id }))}
                        data-testid={`digest-tpl-${t.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          digestSchedule.template === t.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400">
                  {digestSchedule.frequency === 'daily' && `Every day at ${String(digestSchedule.hour).padStart(2,'0')}:${String(digestSchedule.minute).padStart(2,'0')} SGT`}
                  {digestSchedule.frequency === 'weekly' && `Every ${['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][parseInt(digestSchedule.day_of_week)||0]} at ${String(digestSchedule.hour).padStart(2,'0')}:${String(digestSchedule.minute).padStart(2,'0')} SGT`}
                  {digestSchedule.frequency === 'monthly' && `${digestSchedule.day_of_month}${['st','nd','rd'][parseInt(digestSchedule.day_of_month)-1]||'th'} of each month at ${String(digestSchedule.hour).padStart(2,'0')}:${String(digestSchedule.minute).padStart(2,'0')} SGT`}
                </p>
              </div>

              <div className="flex gap-2 items-center flex-wrap">
                <button onClick={() => loadDigestPreview()} disabled={previewLoading}
                  data-testid="preview-digest-btn"
                  className="flex items-center gap-2 text-xs bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-300 font-medium">
                  <Eye className="w-3.5 h-3.5" />
                  {previewLoading ? 'Loading...' : 'Preview Digest'}
                </button>
                <button onClick={sendDigestNow} disabled={digestSending}
                  data-testid="send-digest-btn"
                  className="flex items-center gap-2 text-xs bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 disabled:bg-gray-300 font-medium">
                  <Mail className="w-3.5 h-3.5" />
                  {digestSending ? 'Sending...' : 'Send Digest Now'}
                </button>
                <button onClick={() => openDigestInNewTab()}
                  data-testid="open-digest-tab-btn"
                  className="flex items-center gap-2 text-xs border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-50 font-medium">
                  <FileText className="w-3.5 h-3.5" />Open in New Tab
                </button>
              </div>
              {digestMsg && (
                <p className={`text-xs mt-2 ${digestMsg.includes('sent') || digestMsg.includes('saved') || digestMsg.includes('Saved') ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="digest-msg">
                  {digestMsg}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button onClick={() => { setShowHistory(v => !v); if (!showHistory) loadNotifHistory(); }}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-sm">Notification History</span>
            {notifHistory.length > 0 && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{notifHistory.length}</span>
            )}
          </div>
          {showHistory ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {showHistory && (
          <div className="border-t">
            {historyLoading ? (
              <div className="p-6 text-center text-gray-400 text-sm"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</div>
            ) : notifHistory.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No notifications sent yet. Send a test email or SMS to see history.</div>
            ) : (
              <>
                <div className="px-5 py-2 bg-gray-50 flex justify-between items-center">
                  <span className="text-[10px] text-gray-400">{notifHistory.length} records</span>
                  <button onClick={loadNotifHistory} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />Refresh
                  </button>
                </div>
                <div className="divide-y max-h-72 overflow-y-auto">
                  {notifHistory.map((n, i) => (
                    <div key={i} className="flex items-start gap-3 px-5 py-3" data-testid="notif-history-row">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${n.type === 'email' ? 'bg-blue-50' : 'bg-purple-50'}`}>
                        {n.type === 'email' ? <Mail className="w-3.5 h-3.5 text-blue-500" /> : <Phone className="w-3.5 h-3.5 text-purple-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{n.subject || 'Notification'}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${n.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : n.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                            {n.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          To: {n.recipient} · {n.type.toUpperCase()} · by {n.sent_by || 'system'}
                        </p>
                        {n.error && <p className="text-xs text-red-500 mt-0.5">{n.error}</p>}
                      </div>
                      <span className="text-[10px] text-gray-400 shrink-0 mt-1">
                        {n.timestamp?.replace('T', ' ').slice(0, 19)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Confirm delivery modal */}
      {confirmModal && <ConfirmModal delivery={confirmModal} onConfirm={confirmDelivery} onClose={() => setConfirmModal(null)} cur={cur} />}

      {/* Cancel Confirmation Modal */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => !cancelLoading && setCancelModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="cancel-modal">
            {cancelLoading ? (
              <div className="flex flex-col items-center py-6">
                <svg className="animate-spin h-10 w-10 text-orange-500 mb-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                <p className="text-lg font-bold text-gray-900">Cancelling...</p>
                <p className="text-sm text-gray-500 mt-1">{cancelModal.name}</p>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-center text-gray-900">
                  Cancel {cancelModal.type === 'po' ? 'Purchase Order' : 'Delivery Order'}?
                </h3>
                <p className="text-sm text-gray-500 text-center mt-2">
                  <strong>{cancelModal.name}</strong>
                  {cancelModal.type === 'po' && <><br />The linked Delivery Order will also be cancelled.</>}
                </p>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setCancelModal(null)}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    Keep
                  </button>
                  <button onClick={() => cancelModal.type === 'po' ? cancelPO(cancelModal.id) : cancelDO(cancelModal.id)}
                    className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors">
                    Yes, Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create PO Modal */}
      {showCreatePO && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowCreatePO(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="create-po-modal">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-semibold text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" />Create Purchase Order</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Product</label>
                <select value={newPO.product_id} onChange={async (e) => {
                  const pid = e.target.value;
                  const p = productsList.find(x => x.product_id === pid);
                  setNewPO(prev => ({ ...prev, product_id: pid, unit_cost: p?.cost || p?.price || prev.unit_cost, supplier_id: '' }));
                  if (pid) {
                    try { const r = await axios.get(`${API}/product-suppliers/${pid}`); setProductSuppliers(r.data); } catch { setProductSuppliers([]); }
                  } else { setProductSuppliers([]); }
                }} className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none" data-testid="po-product-select">
                  <option value="">Select product...</option>
                  {productsList.map(p => <option key={p.product_id} value={p.product_id}>{p.name} ({p.sku}) — Stock: {p.stock}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Supplier</label>
                <select value={newPO.supplier_id} onChange={e => {
                  const sid = e.target.value;
                  const mapped = productSuppliers.find(ps => ps.supplier_id === sid);
                  if (mapped) {
                    setNewPO(prev => ({ ...prev, supplier_id: sid, unit_cost: mapped.unit_cost || prev.unit_cost }));
                  } else {
                    setNewPO(prev => ({ ...prev, supplier_id: sid }));
                  }
                }}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none" data-testid="po-supplier-select">
                  <option value="">Select supplier...</option>
                  {productSuppliers.length > 0 && (
                    <optgroup label="Carries this product">
                      {productSuppliers.map(ps => (
                        <option key={ps.supplier_id} value={ps.supplier_id}>
                          {ps.supplier_name} (S${ps.unit_cost} · {ps.lead_time_days}d)
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label={productSuppliers.length > 0 ? "Other suppliers" : "All suppliers"}>
                    {suppliersList.filter(s => !productSuppliers.some(ps => ps.supplier_id === s.supplier_id)).map(s => (
                      <option key={s.supplier_id} value={s.supplier_id}>{s.name} (Lead: {s.lead_time_days || 7}d)</option>
                    ))}
                  </optgroup>
                </select>
                {productSuppliers.length === 0 && newPO.product_id && (
                  <p className="text-[10px] text-gray-400 mt-1">No mapped suppliers. Go to Suppliers tab to add product-supplier mappings.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Quantity</label>
                  <input type="number" min="1" value={newPO.quantity} placeholder="100"
                    onChange={e => setNewPO(prev => ({ ...prev, quantity: e.target.value }))}
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none" data-testid="po-quantity-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Unit Cost ({cur})</label>
                  <input type="number" min="0" step="0.01" value={newPO.unit_cost} placeholder="10.00"
                    onChange={e => setNewPO(prev => ({ ...prev, unit_cost: e.target.value }))}
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none" data-testid="po-unitcost-input" />
                </div>
              </div>
              {newPO.quantity && newPO.unit_cost && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                  Total: <strong className="text-gray-900">{cur}{(parseFloat(newPO.quantity) * parseFloat(newPO.unit_cost)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                </p>
              )}
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Reason (optional)</label>
                <input type="text" value={newPO.reason} placeholder="e.g. Restock for upcoming sale"
                  onChange={e => setNewPO(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowCreatePO(false)}
                className="px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={submitCreatePO} disabled={createLoading || !newPO.product_id || !newPO.supplier_id || !newPO.quantity || !newPO.unit_cost}
                className="px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1.5">
                {createLoading ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Creating...</> : <><Plus className="w-3.5 h-3.5" />Create PO</>}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Digest Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          data-testid="digest-preview-modal" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[780px] max-h-[85vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 shrink-0">
              <div className="flex items-center gap-3">
                <Eye className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-sm">Digest Preview</span>
                {previewStats && (
                  <span className="text-[10px] text-gray-400">
                    {previewStats.orders} orders · S${(previewStats.revenue || 0).toLocaleString()} revenue · {previewStats.total_products} products
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openDigestInNewTab(previewTemplate)}
                  data-testid="preview-open-newtab"
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium">
                  <FileText className="w-3 h-3" />Open in new tab
                </button>
                <button onClick={() => setPreviewOpen(false)} className="p-1 hover:bg-gray-200 rounded-lg">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
            {/* Template tabs */}
            <div className="flex border-b bg-white px-5 pt-2 gap-1 shrink-0">
              {[
                { id: 'executive', label: 'Executive Summary' },
                { id: 'full', label: 'Full Operations' },
                { id: 'critical', label: 'Critical Only' },
              ].map(t => (
                <button key={t.id}
                  onClick={() => loadDigestPreview(t.id)}
                  data-testid={`preview-tab-${t.id}`}
                  className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                    previewTemplate === t.id
                      ? 'border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* Body - rendered HTML */}
            <div className="flex-1 overflow-y-auto p-1">
              <iframe
                srcDoc={previewHtml}
                title="Digest Preview"
                className="w-full border-0"
                style={{ minHeight: '500px', height: '100%' }}
                data-testid="digest-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
